import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@material-ui/core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PropTypes from 'prop-types';

export const LRU_OPTIONS_TITLE = 'General Settings';
const defaultVMNameHelperText = 'The name of the VM';
export const vmNameExistsError = 'This VM name has already been added, please choose a new vm name';

const vmNameMaxLength = 20;
const validVMName = new RegExp(`^(?![0-9]+$)(?!.*-$)(?!-)[a-z0-9-]{1,${vmNameMaxLength}}$`);
export const vmNameInputErrHelperText = `VM name must contain only lowercase alphanumeric characters or '-', and start and end with an alphanumeric number.`;
export const vmNameInputErrEmptyText = "VM name can't be empty";
export const vmNameTooLongErrHelperText = 'VM name must contain no more than 20 characters';

// checks if the vm name is valid
export function ValidVMName(newName, addedVMList) {
  const matchesRegex = validVMName.test(newName);
  const isDuplicate = addedVMList.some((vm) => vm.name === newName);

  return { isValid: matchesRegex && !isDuplicate, matchesRegex, isDuplicate };
}

VMGeneralSettings.propTypes = {
  currentVM: PropTypes.object,
  updateCurrentVM: PropTypes.func,
  addedVMList: PropTypes.array
};

export function VMGeneralSettings({ currentVM, updateCurrentVM, addedVMList }) {
  const {
    isValid: isValidVMName,
    matchesRegex,
    isDuplicate
  } = ValidVMName(currentVM.name, addedVMList);

  let vmNameHelperText = defaultVMNameHelperText;

  if (currentVM.name.length > vmNameMaxLength) {
    vmNameHelperText = vmNameTooLongErrHelperText;
  } else if (currentVM.name === '') {
    vmNameHelperText = vmNameInputErrEmptyText;
  } else if (!matchesRegex) {
    vmNameHelperText = vmNameInputErrHelperText;
  } else if (isDuplicate) {
    vmNameHelperText = vmNameExistsError;
  }

  const vmNameError = !isValidVMName;

  const handleNameChange = (event) => {
    const newName = event.target.value;
    const updatedVM = {
      ...currentVM,
      name: newName
    };
    updateCurrentVM(updatedVM);
  };

  const handleReplicasChange = (event) => {
    const updatedVM = {
      ...currentVM,
      replicas: event.target.value
    };
    updateCurrentVM(updatedVM);
  };

  const handleArchChange = (event) => {
    const updatedVM = {
      ...currentVM,
      architecture: event.target.value
    };
    updateCurrentVM(updatedVM);
  };

  return (
    <Grid item xs={12}>
      <Accordion defaultExpanded data-testid="vm-general-settings">
        <AccordionSummary sx={{ paddingLeft: 0 }} expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">{LRU_OPTIONS_TITLE}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                data-testid="vm-name"
                label="VM Name"
                variant="outlined"
                fullWidth
                value={currentVM.name}
                helperText={vmNameHelperText}
                onChange={handleNameChange}
                error={vmNameError}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="arch-select-label">Architecture</InputLabel>
                <Select
                  labelId="arch-select-label"
                  id="arch-select"
                  value={currentVM?.architecture}
                  label="Architecture"
                  onChange={handleArchChange}
                >
                  <MenuItem value="amd64">amd64</MenuItem>
                  <MenuItem value="arm64">arm64</MenuItem>
                </Select>
              </FormControl>
              <FormHelperText>The architecture of the VM</FormHelperText>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="replicas-select-label">Replicas</InputLabel>
                <Select
                  labelId="replicas-select-label"
                  id="replicas-select"
                  value={currentVM?.replicas}
                  label="Replicas"
                  onChange={handleReplicasChange}
                >
                  {Array.from({ length: currentVM.max_replicas }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormHelperText>How many instances of this VM to deploy</FormHelperText>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}
